// audio-call.js — session WebRTC média 1-à-1 (audio ou vidéo).
// Le média est chiffré par DTLS-SRTP entre navigateurs. Le serveur applicatif
// ne reçoit que des signaux E2E chiffrés ; un TURN éventuel ne fait que relayer
// les paquets déjà chiffrés.
export const DEFAULT_AUDIO_CONSTRAINTS = Object.freeze({
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
  },
  video: false,
});

export const DEFAULT_VIDEO_CONSTRAINTS = Object.freeze({
  audio: DEFAULT_AUDIO_CONSTRAINTS.audio,
  video: {
    facingMode: 'user',
    width: { ideal: 1280, max: 1280 },
    height: { ideal: 720, max: 720 },
    frameRate: { ideal: 24, max: 30 },
  },
});

const ICE_GATHERING_TIMEOUT_MS = 12000;
const ICE_FAILURE_GRACE_MS = 4500;
const QUALITY_INTERVAL_MS = 5000;

export function supportsAudioCalls(scope = globalThis) {
  return typeof scope.RTCPeerConnection === 'function'
    && !!scope.navigator?.mediaDevices?.getUserMedia;
}

export function supportsVideoCalls(scope = globalThis) {
  return supportsAudioCalls(scope);
}

export function normalizeRtcConfig(raw) {
  const cleanUrl = (url) => typeof url === 'string'
    && /^(stun|stuns|turn|turns):/i.test(url)
    && url.length <= 1024 ? url : '';
  const servers = Array.isArray(raw?.iceServers) ? raw.iceServers
    .filter((s) => s && (typeof s.urls === 'string' || Array.isArray(s.urls)))
    .slice(0, 8)
    .map((s) => {
      const urls = (Array.isArray(s.urls) ? s.urls : [s.urls]).map(cleanUrl).filter(Boolean).slice(0, 8);
      if (!urls.length) return null;
      return {
        urls: Array.isArray(s.urls) ? urls : urls[0],
        ...(typeof s.username === 'string' && s.username.length <= 512 ? { username: s.username } : {}),
        ...(typeof s.credential === 'string' && s.credential.length <= 1024 ? { credential: s.credential } : {}),
      };
    }).filter(Boolean) : [];
  const iceTransportPolicy = raw?.iceTransportPolicy === 'relay' ? 'relay' : 'all';
  return {
    iceServers: servers,
    iceTransportPolicy,
    bundlePolicy: 'max-bundle',
    rtcpMuxPolicy: 'require',
    // En mode normal (`all`), un petit pool accélère l'établissement P2P/STUN
    // tout en gardant TURN comme secours. Le mode relay-only reste à zéro car
    // les pré-allocations TURN ont été moins prévisibles sur Safari/iOS.
    iceCandidatePoolSize: iceTransportPolicy === 'relay' ? 0 : (Number.isInteger(raw?.iceCandidatePoolSize)
      ? Math.max(0, Math.min(10, raw.iceCandidatePoolSize)) : 0),
  };
}

const CANDIDATE_TYPES = new Set(['host', 'srflx', 'prflx', 'relay']);

function safeCandidateType(value) {
  const type = String(value || '').toLowerCase();
  return CANDIDATE_TYPES.has(type) ? type : 'unknown';
}

function safeTransport(value) {
  const protocol = String(value || '').toLowerCase();
  return ['udp', 'tcp'].includes(protocol) ? protocol : 'unknown';
}

// Ne retourne volontairement aucune IP, aucun port, aucun pseudo et aucun callId.
// Cette synthèse peut donc être conservée localement pour le diagnostic sans
// transformer la page Santé en journal de métadonnées sensibles.
export function summarizeSelectedCandidatePair(report) {
  if (!report) return null;
  const stats = [];
  if (typeof report.forEach === 'function') report.forEach((value) => stats.push(value));
  else if (typeof report[Symbol.iterator] === 'function') {
    for (const value of report) stats.push(Array.isArray(value) ? value[1] : value);
  } else if (Array.isArray(report)) stats.push(...report);
  if (!stats.length) return null;

  const byId = new Map(stats.filter(Boolean).map((item) => [item.id, item]));
  let pair = null;
  for (const item of stats) {
    if (item?.type === 'transport' && item.selectedCandidatePairId) {
      pair = byId.get(item.selectedCandidatePairId) || null;
      if (pair) break;
    }
  }
  if (!pair) pair = stats.find((item) => item?.type === 'candidate-pair' && item.state === 'succeeded' && item.nominated) || null;
  if (!pair) return null;

  const local = byId.get(pair.localCandidateId) || null;
  const remote = byId.get(pair.remoteCandidateId) || null;
  const localType = safeCandidateType(local?.candidateType);
  const remoteType = safeCandidateType(remote?.candidateType);
  const usesTurn = localType === 'relay' || remoteType === 'relay';
  const protocol = safeTransport(local?.protocol || remote?.protocol || pair?.protocol);
  const relayProtocol = usesTurn ? safeTransport(local?.relayProtocol || remote?.relayProtocol) : 'unknown';
  return {
    route: usesTurn ? 'turn' : 'p2p',
    localCandidateType: localType,
    remoteCandidateType: remoteType,
    protocol,
    relayProtocol,
    at: Date.now(),
  };
}

function clampMetric(value, min, max, digits = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const bounded = Math.min(max, Math.max(min, n));
  const factor = 10 ** digits;
  return Math.round(bounded * factor) / factor;
}

// Qualité média purement locale. Ne retourne aucune adresse, aucun port,
// identifiant d'appel ou pseudo. Les seuils sont volontairement simples :
// l'objectif est une indication UX, pas une télémétrie serveur.
export function summarizeCallQuality(report) {
  if (!report) return null;
  const stats = [];
  if (typeof report.forEach === 'function') report.forEach((value) => stats.push(value));
  else if (typeof report[Symbol.iterator] === 'function') {
    for (const value of report) stats.push(Array.isArray(value) ? value[1] : value);
  } else if (Array.isArray(report)) stats.push(...report);
  if (!stats.length) return null;

  const pairs = stats.filter((item) => item?.type === 'candidate-pair' && item.state === 'succeeded');
  const pair = pairs.find((item) => item.nominated) || pairs[0] || null;
  const inbound = stats.find((item) => item?.type === 'inbound-rtp' && !item.isRemote
    && (item.kind === 'audio' || item.mediaType === 'audio')) || null;
  const remoteInbound = stats.find((item) => item?.type === 'remote-inbound-rtp'
    && (item.kind === 'audio' || item.mediaType === 'audio')) || null;

  const rttSeconds = pair?.currentRoundTripTime ?? remoteInbound?.roundTripTime;
  const rttMs = clampMetric(Number(rttSeconds) * 1000, 0, 5000, 0);
  const jitterMs = clampMetric(Number(inbound?.jitter) * 1000, 0, 2000, 0);
  const received = Math.max(0, Number(inbound?.packetsReceived || 0));
  const lost = Math.max(0, Number(inbound?.packetsLost || 0));
  const packetLossPct = received + lost > 0 ? clampMetric((lost / (received + lost)) * 100, 0, 100, 1) : null;

  if (rttMs == null && jitterMs == null && packetLossPct == null) return null;
  const bad = (packetLossPct ?? 0) >= 8 || (rttMs ?? 0) >= 600 || (jitterMs ?? 0) >= 120;
  const weak = (packetLossPct ?? 0) >= 3 || (rttMs ?? 0) >= 350 || (jitterMs ?? 0) >= 70;
  const good = (packetLossPct ?? 0) >= 1 || (rttMs ?? 0) >= 180 || (jitterMs ?? 0) >= 35;
  return {
    level: bad ? 'poor' : weak ? 'weak' : good ? 'good' : 'excellent',
    rttMs,
    jitterMs,
    packetLossPct,
    at: Date.now(),
  };
}

export class AudioCallSession {
  constructor({
    callId,
    peer,
    sendSignal,
    rtcConfig,
    onState = () => {},
    onRemoteStream = () => {},
    onLocalStream = () => {},
    onDiagnostic = () => {},
    onQuality = () => {},
    onError = () => {},
    rtcFactory = (cfg) => new RTCPeerConnection(cfg),
    mediaDevices = globalThis.navigator?.mediaDevices,
    mode = 'audio',
  }) {
    if (!callId || !peer || typeof sendSignal !== 'function') throw new TypeError('Session d’appel invalide.');
    if (!mediaDevices?.getUserMedia) throw new Error('Microphone/caméra indisponible sur ce navigateur.');
    this.callId = String(callId);
    this.peer = String(peer);
    this.sendSignal = sendSignal;
    this.onState = onState;
    this.onRemoteStream = onRemoteStream;
    this.onLocalStream = onLocalStream;
    this.onDiagnostic = onDiagnostic;
    this.onQuality = onQuality;
    this.onError = onError;
    this.rtcFactory = rtcFactory;
    this.mediaDevices = mediaDevices;
    this.mode = mode === 'video' ? 'video' : 'audio';
    this.rtcConfig = normalizeRtcConfig(rtcConfig);
    this.pc = null;
    this.localStream = null;
    this.remoteStream = null;
    this.pendingCandidates = [];
    this.pendingLocalCandidates = [];
    this.initialDescriptionSent = false;
    this.failureTimer = null;
    this.closed = false;
    this.muted = false;
    this.videoEnabled = this.mode === 'video';
    this.facingMode = 'user';
    this.networkDiagnosticSent = false;
    this.qualityTimer = null;
  }

  stopQualityMonitor() {
    if (this.qualityTimer) clearInterval(this.qualityTimer);
    this.qualityTimer = null;
  }

  startQualityMonitor() {
    if (this.closed || !this.pc?.getStats || this.qualityTimer) return;
    const sample = async () => {
      if (this.closed || !this.pc?.getStats) return;
      try {
        const quality = summarizeCallQuality(await this.pc.getStats());
        if (quality && !this.closed) this.onQuality(quality);
      } catch (_) { /* diagnostic non bloquant */ }
    };
    sample();
    this.qualityTimer = setInterval(sample, QUALITY_INTERVAL_MS);
  }

  cancelFailureTimer() {
    if (this.failureTimer) clearTimeout(this.failureTimer);
    this.failureTimer = null;
  }

  scheduleIceFailure(message) {
    if (this.closed || this.failureTimer) return;
    this.failureTimer = setTimeout(() => {
      this.failureTimer = null;
      if (this.closed || !this.pc) return;
      const failed = this.pc.connectionState === 'failed' || this.pc.iceConnectionState === 'failed';
      if (!failed) return;
      this.onError(new Error(message));
      this.onState('failed');
    }, ICE_FAILURE_GRACE_MS);
  }

  sendLocalCandidate(candidate) {
    Promise.resolve(this.sendSignal({ action: 'candidate', candidate }))
      .catch((error) => this.onError(error));
  }

  async emitNetworkDiagnostic() {
    if (this.closed || this.networkDiagnosticSent || !this.pc?.getStats) return null;
    try {
      const summary = summarizeSelectedCandidatePair(await this.pc.getStats());
      if (!summary || this.closed) return null;
      this.networkDiagnosticSent = true;
      this.onDiagnostic(summary);
      return summary;
    } catch (_) {
      // Le diagnostic ne doit jamais faire échouer un appel qui fonctionne.
      return null;
    }
  }

  ensurePeerConnection() {
    if (this.closed) throw new Error('Appel terminé.');
    if (this.pc) return this.pc;
    const pc = this.rtcFactory(this.rtcConfig);
    pc.onicecandidate = ({ candidate }) => {
      if (!candidate || this.closed) return;
      const clean = candidate.toJSON?.() || candidate;
      // L'offre/réponse doit toujours précéder les candidats trickle. Les
      // candidats recueillis avant sont aussi incorporés dans le SDP final.
      if (!this.initialDescriptionSent) {
        if (this.pendingLocalCandidates.length < 128) this.pendingLocalCandidates.push(clean);
        return;
      }
      this.sendLocalCandidate(clean);
    };
    pc.ontrack = ({ streams, track }) => {
      let stream = streams?.[0] || null;
      if (!stream && typeof MediaStream === 'function') {
        stream = this.remoteStream || new MediaStream();
        try {
          const already = stream.getTracks?.().some((t) => t.id && track?.id && t.id === track.id);
          if (!already) stream.addTrack?.(track);
        } catch (_) {}
      }
      if (stream) {
        this.remoteStream = stream;
        this.onRemoteStream(stream);
      }
    };
    pc.onconnectionstatechange = () => {
      const state = pc.connectionState || 'unknown';
      if (state === 'connected') {
        this.cancelFailureTimer();
        this.emitNetworkDiagnostic();
        this.startQualityMonitor();
      }
      if (state === 'failed') {
        this.stopQualityMonitor();
        this.scheduleIceFailure('Connexion d’appel impossible.');
        return;
      }
      this.onState(state);
      if (state === 'closed') this.close(false);
    };
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      // Filet pour Safari qui a historiquement exposé iceConnectionState avant
      // connectionState. Les doublons d'état sont idempotents côté contrôleur.
      if (state === 'connected' || state === 'completed') {
        this.cancelFailureTimer();
        this.onState('connected');
        this.emitNetworkDiagnostic();
        this.startQualityMonitor();
      } else if (state === 'checking') {
        this.cancelFailureTimer();
        this.onState('connecting');
      } else if (state === 'disconnected') {
        this.stopQualityMonitor();
        this.onState('disconnected');
      } else if (state === 'failed') {
        this.stopQualityMonitor();
        this.scheduleIceFailure('Le réseau ne permet pas d’établir l’appel.');
      }
    };
    this.pc = pc;
    return pc;
  }

  async waitForIceGatheringComplete(timeoutMs = ICE_GATHERING_TIMEOUT_MS) {
    const pc = this.ensurePeerConnection();
    if (!pc.iceGatheringState || pc.iceGatheringState === 'complete') return true;
    return new Promise((resolve) => {
      let settled = false;
      let timer = null;
      let propertyHandler = null;
      const finish = (complete) => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        if (typeof pc.removeEventListener === 'function') pc.removeEventListener('icegatheringstatechange', onChange);
        else if (propertyHandler && pc.onicegatheringstatechange === propertyHandler) pc.onicegatheringstatechange = previous || null;
        resolve(complete);
      };
      const onChange = () => {
        if (pc.iceGatheringState === 'complete') finish(true);
      };
      const previous = pc.onicegatheringstatechange;
      if (typeof pc.addEventListener === 'function') pc.addEventListener('icegatheringstatechange', onChange);
      else {
        propertyHandler = (...args) => {
          try { previous?.apply(pc, args); } catch (_) {}
          onChange();
        };
        pc.onicegatheringstatechange = propertyHandler;
      }
      timer = setTimeout(() => finish(pc.iceGatheringState === 'complete'), Math.max(1000, timeoutMs));
      onChange();
    });
  }

  async announceLocalDescription(action) {
    const pc = this.ensurePeerConnection();
    await this.waitForIceGatheringComplete();
    if (this.closed || !pc.localDescription) throw new Error('Description média locale indisponible.');

    // Tout ce qui a été recueilli jusqu'ici est déjà inclus dans le SDP que
    // l'on photographie. On conserve uniquement les candidats qui arriveraient
    // pendant l'envoi asynchrone de cette description.
    const description = { type: pc.localDescription.type, sdp: pc.localDescription.sdp };
    this.pendingLocalCandidates.splice(0);
    await this.sendSignal({ action, description });
    this.initialDescriptionSent = true;

    const lateCandidates = this.pendingLocalCandidates.splice(0);
    for (const candidate of lateCandidates) this.sendLocalCandidate(candidate);
  }

  async acquireMedia() {
    if (this.closed) throw new Error('Appel terminé.');
    if (this.localStream) return this.localStream;
    let stream;
    const preferred = this.mode === 'video' ? DEFAULT_VIDEO_CONSTRAINTS : DEFAULT_AUDIO_CONSTRAINTS;
    const fallback = this.mode === 'video' ? { audio: true, video: true } : { audio: true, video: false };
    try {
      stream = await this.mediaDevices.getUserMedia(preferred);
    } catch (error) {
      // Retente uniquement les erreurs de contraintes. Un refus de permission
      // utilisateur n'est jamais contourné ou redemandé en boucle.
      if (!['OverconstrainedError', 'ConstraintNotSatisfiedError', 'TypeError'].includes(error?.name)) throw error;
      stream = await this.mediaDevices.getUserMedia(fallback);
    }
    if (this.closed) {
      stream.getTracks?.().forEach((track) => track.stop());
      throw new Error('Appel terminé.');
    }
    const pc = this.ensurePeerConnection();
    const audioTracks = stream.getAudioTracks?.() || [];
    const videoTracks = stream.getVideoTracks?.() || [];
    if (!audioTracks.length) {
      stream.getTracks?.().forEach((track) => track.stop());
      throw new Error('Aucune piste microphone disponible.');
    }
    if (this.mode === 'video' && !videoTracks.length) {
      stream.getTracks?.().forEach((track) => track.stop());
      throw new Error('Aucune piste caméra disponible.');
    }
    for (const track of stream.getTracks?.() || audioTracks) pc.addTrack(track, stream);
    this.localStream = stream;
    this.setMuted(this.muted);
    this.setVideoEnabled(this.videoEnabled);
    this.onLocalStream(stream);
    return stream;
  }

  // Alias conservé pour les anciens tests/appelants audio.
  async acquireMicrophone() { return this.acquireMedia(); }

  async startOutgoing() {
    const pc = this.ensurePeerConnection();
    await this.acquireMedia();
    const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: this.mode === 'video' });
    await pc.setLocalDescription(offer);
    await this.announceLocalDescription('offer');
    this.onState('calling');
  }

  async acceptOffer(description) {
    const pc = this.ensurePeerConnection();
    await pc.setRemoteDescription(description);
    await this.flushCandidates();
    await this.acquireMedia();
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await this.announceLocalDescription('answer');
    this.onState('connecting');
  }

  async applyAnswer(description) {
    const pc = this.ensurePeerConnection();
    if (pc.currentRemoteDescription || pc.remoteDescription) return;
    await pc.setRemoteDescription(description);
    await this.flushCandidates();
    this.onState('connecting');
  }

  async addRemoteCandidate(candidate) {
    if (this.closed || !candidate) return;
    const pc = this.ensurePeerConnection();
    if (!pc.remoteDescription) {
      if (this.pendingCandidates.length < 256) this.pendingCandidates.push(candidate);
      return;
    }
    try { await pc.addIceCandidate(candidate); }
    catch (error) { this.onError(error); }
  }

  async flushCandidates() {
    const pc = this.ensurePeerConnection();
    if (!pc.remoteDescription || !this.pendingCandidates.length) return;
    const queued = this.pendingCandidates.splice(0);
    for (const candidate of queued) {
      try { await pc.addIceCandidate(candidate); }
      catch (error) { this.onError(error); }
    }
  }

  async restartIceAsOffer() {
    const pc = this.ensurePeerConnection();
    if (this.closed) throw new Error('Appel terminé.');
    this.cancelFailureTimer();
    this.stopQualityMonitor();
    this.initialDescriptionSent = false;
    this.pendingLocalCandidates = [];
    this.networkDiagnosticSent = false;
    try { pc.restartIce?.(); } catch (_) {}
    const offer = await pc.createOffer({ iceRestart: true, offerToReceiveAudio: true, offerToReceiveVideo: this.mode === 'video' });
    await pc.setLocalDescription(offer);
    await this.announceLocalDescription('restart-offer');
    return true;
  }

  async acceptRestartOffer(description) {
    const pc = this.ensurePeerConnection();
    if (this.closed) throw new Error('Appel terminé.');
    this.cancelFailureTimer();
    this.stopQualityMonitor();
    this.networkDiagnosticSent = false;
    await pc.setRemoteDescription(description);
    await this.flushCandidates();
    this.initialDescriptionSent = false;
    this.pendingLocalCandidates = [];
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await this.announceLocalDescription('restart-answer');
    this.onState('connecting');
    return true;
  }

  async applyRestartAnswer(description) {
    const pc = this.ensurePeerConnection();
    if (this.closed) throw new Error('Appel terminé.');
    this.networkDiagnosticSent = false;
    await pc.setRemoteDescription(description);
    await this.flushCandidates();
    this.onState('connecting');
    return true;
  }

  setMuted(muted) {
    this.muted = !!muted;
    for (const track of this.localStream?.getAudioTracks?.() || []) track.enabled = !this.muted;
    return this.muted;
  }

  setVideoEnabled(enabled) {
    this.videoEnabled = this.mode === 'video' && !!enabled;
    for (const track of this.localStream?.getVideoTracks?.() || []) track.enabled = this.videoEnabled;
    return this.videoEnabled;
  }

  async switchCamera() {
    if (this.closed || this.mode !== 'video') return this.facingMode;
    const pc = this.ensurePeerConnection();
    const oldTrack = this.localStream?.getVideoTracks?.()[0];
    if (!oldTrack) throw new Error('Caméra locale indisponible.');
    const nextFacing = this.facingMode === 'environment' ? 'user' : 'environment';
    let replacementStream;
    try {
      replacementStream = await this.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: nextFacing }, width: { ideal: 1280, max: 1280 }, height: { ideal: 720, max: 720 } },
      });
    } catch (error) {
      if (!['OverconstrainedError', 'ConstraintNotSatisfiedError', 'TypeError'].includes(error?.name)) throw error;
      replacementStream = await this.mediaDevices.getUserMedia({ audio: false, video: true });
    }
    const newTrack = replacementStream.getVideoTracks?.()[0];
    if (!newTrack) {
      replacementStream.getTracks?.().forEach((t) => t.stop());
      throw new Error('Impossible de changer de caméra.');
    }
    const sender = pc.getSenders?.().find((item) => item.track?.kind === 'video');
    if (!sender?.replaceTrack) {
      replacementStream.getTracks?.().forEach((t) => t.stop());
      throw new Error('Changement de caméra non pris en charge.');
    }
    await sender.replaceTrack(newTrack);
    try { this.localStream.removeTrack?.(oldTrack); } catch (_) {}
    try { this.localStream.addTrack?.(newTrack); } catch (_) {}
    try { oldTrack.stop(); } catch (_) {}
    for (const t of replacementStream.getTracks?.() || []) if (t !== newTrack) { try { t.stop(); } catch (_) {} }
    newTrack.enabled = this.videoEnabled;
    this.facingMode = nextFacing;
    this.onLocalStream(this.localStream);
    return this.facingMode;
  }

  async close(notify = true, reason = 'hangup') {
    if (this.closed) return;
    this.closed = true;
    this.cancelFailureTimer();
    this.stopQualityMonitor();
    if (notify) {
      try { await this.sendSignal({ action: 'hangup', reason }); } catch (_) {}
    }
    try {
      this.pc?.getSenders?.().forEach((sender) => { try { sender.track?.stop(); } catch (_) {} });
      this.pc?.close();
    } catch (_) {}
    for (const track of this.localStream?.getTracks?.() || []) { try { track.stop(); } catch (_) {} }
    for (const track of this.remoteStream?.getTracks?.() || []) { try { track.stop(); } catch (_) {} }
    this.pendingCandidates = [];
    this.pendingLocalCandidates = [];
    this.pc = null;
    this.localStream = null;
    this.remoteStream = null;
    this.onRemoteStream(null);
    this.onLocalStream(null);
    this.onState('ended');
  }
}
